import { useEffect, useMemo, useState } from "react";
import { api, timeShort } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Pause, Play, Timer, SkipForward } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const RING = 2 * Math.PI * 26;

/** Live "now / next up" bar driven by today's packed schedule. */
export default function FocusBar({ data, onChanged }) {
  const [now, setNow] = useState(() => new Date());
  const [paused, setPaused] = useState(() => localStorage.getItem("halo_focus_paused") === "1");

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, [paused]);

  const blocks = useMemo(() => ([
    ...(data.schedule || []).map((e) => ({ id: e.id, kind: "event", title: e.title, start: e.start, end: e.end })),
    ...(data.tasks_today || []).filter((t) => t.scheduled_for && t.status !== "done").map((t) => ({
      id: t.id,
      kind: "task",
      title: t.title,
      start: t.scheduled_for,
      end: new Date(new Date(t.scheduled_for).getTime() + (t.duration_minutes || 30) * 60000).toISOString(),
      duration: t.duration_minutes || 30,
    })),
  ]).sort((a, b) => new Date(a.start) - new Date(b.start)), [data.schedule, data.tasks_today]);

  const current = blocks.find((b) => new Date(b.start) <= now && now < new Date(b.end));
  const next = blocks.find((b) => new Date(b.start) > now);

  const togglePause = () => {
    const v = !paused;
    setPaused(v);
    localStorage.setItem("halo_focus_paused", v ? "1" : "0");
    toast.success(v ? "Focus paused — nothing will tick along" : "Focus resumed");
  };

  const complete = async (b) => {
    try {
      await api.put(`/tasks/${b.id}`, { status: "done" });
      toast.success("Done — on to the next one");
      onChanged?.();
    } catch { toast.error("Could not update task"); }
  };

  const snooze = async (b) => {
    try {
      const start = new Date(new Date(b.start).getTime() + 15 * 60000);
      await api.put(`/tasks/${b.id}`, { scheduled_for: start.toISOString() });
      toast.success("Pushed back 15 minutes");
      onChanged?.();
    } catch { toast.error("Could not snooze"); }
  };

  if (blocks.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4" data-testid="focus-empty">
        Nothing in the running order yet — press <b>Reschedule Day</b> to pack your slots.
      </div>
    );
  }

  const total = current ? (new Date(current.end) - new Date(current.start)) : 0;
  const elapsed = current ? Math.min(total, now - new Date(current.start)) : 0;
  const progress = total ? elapsed / total : 0;
  const minsLeft = current ? Math.max(0, Math.round((new Date(current.end) - now) / 60000)) : 0;

  return (
    <div className="flex flex-col gap-4 h-full" data-testid="focus-bar">
      {/* Progress ring */}
      <div className="relative w-[68px] h-[68px] shrink-0 mx-auto">
        <svg width="68" height="68" viewBox="0 0 68 68" className="-rotate-90">
          <circle cx="34" cy="34" r="26" fill="none" strokeWidth="5" className="stroke-muted" />
          <circle
            cx="34" cy="34" r="26" fill="none" strokeWidth="5" strokeLinecap="round"
            className={cn(paused ? "stroke-muted-foreground/50" : "stroke-[hsl(var(--studio-primary))]")}
            strokeDasharray={RING}
            strokeDashoffset={RING * (1 - (current ? progress : 0))}
            style={{ transition: "stroke-dashoffset 600ms ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold tabular-nums" data-testid="focus-remaining">
          {current ? `${minsLeft}m` : "—"}
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
          {paused ? "Paused" : current ? "Now" : "Up next"}
        </div>
        <div className="text-lg font-semibold tracking-tight truncate" data-testid="focus-current">
          {current ? current.title : next ? next.title : "Day complete"}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5" data-testid="focus-next">
          {current
            ? `${timeShort(current.start)} — ${timeShort(current.end)}${next ? ` · next: ${next.title} at ${timeShort(next.start)}` : ""}`
            : next
              ? `Starts ${timeShort(next.start)}`
              : "Nothing else scheduled today."}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-auto">
        <Button variant="outline" size="sm" onClick={togglePause} data-testid="focus-pause">
          {paused ? <><Play className="w-3.5 h-3.5 mr-1.5" /> Resume</> : <><Pause className="w-3.5 h-3.5 mr-1.5" /> Pause</>}
        </Button>
        {(current || next)?.kind === "task" && (
          <>
            <Button variant="outline" size="sm" onClick={() => snooze(current || next)} data-testid="focus-snooze">
              <Timer className="w-3.5 h-3.5 mr-1.5" /> +15m
            </Button>
            <Button
              size="sm"
              onClick={() => complete(current || next)}
              data-testid="focus-done"
              className="bg-[hsl(var(--studio-primary))] hover:bg-[hsl(var(--studio-primary-hover))] text-white"
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark done
            </Button>
          </>
        )}
        {(current || next)?.kind === "event" && (
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
            <SkipForward className="w-3.5 h-3.5" /> calendar event
          </span>
        )}
      </div>
    </div>
  );
}
